import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Edit2, Send, Package, PackageOpen, XCircle, Printer,
  ArrowLeft, Clock, Check, ArrowRight,
} from 'lucide-react';
import Layout from '../../components/Layout';
import { useAuth } from '../../context/AuthContext';
import { stockTransferService } from '../../services/stockTransferService';
import unitConversionService, { UnitConversion } from '../../services/unitConversionService';
import {
  StockTransferDTO,
  StockTransferApprovalStatusDTO,
  StockTransferApprovalItemDTO,
  StockTransferStatus,
  TransferOutItemRequest,
  TransferInItemRequest,
} from '../../types';
import './StockTransferPage.css';
import '../Products/EntityCreatePage.css';

import { getServerErrorMessage } from '../../utils/errorUtils';
// ─── Status helpers ───────────────────────────────────────────────────────────

const STATUS_LABEL: Record<StockTransferStatus, string> = {
  Draft: 'Draft', PendingApproval: 'Pending Approval', Approved: 'Approved',
  Rejected: 'Rejected', TransferredOut: 'Transferred Out',
  PartiallyReceived: 'Partially Received', Completed: 'Completed', Cancelled: 'Cancelled',
};

const STATUS_CLASS: Record<StockTransferStatus, string> = {
  Draft: 'ims-badge st-badge-draft', PendingApproval: 'ims-badge st-badge-pending',
  Approved: 'ims-badge st-badge-approved', Rejected: 'ims-badge st-badge-rejected',
  TransferredOut: 'ims-badge st-badge-transferredout',
  PartiallyReceived: 'ims-badge st-badge-partiallyreceived',
  Completed: 'ims-badge st-badge-completed', Cancelled: 'ims-badge st-badge-cancelled',
};

// ─── Approval level row ───────────────────────────────────────────────────────

interface ApprovalLevelRowProps {
  approval: StockTransferApprovalItemDTO;
  currentUserId: number;
  onApprove: (id: number) => void;
  onReject:  (id: number) => void;
}

const ApprovalLevelRow: React.FC<ApprovalLevelRowProps> = ({
  approval, currentUserId, onApprove, onReject,
}) => {
  const { t } = useTranslation();
  const isMine = approval.assignedToUserId === currentUserId && approval.status === 'Pending';

  const iconClass =
    approval.status === 'Approved' ? 'st-level-icon--approved' :
    approval.status === 'Rejected' ? 'st-level-icon--rejected' : 'st-level-icon--pending';

  const icon =
    approval.status === 'Approved' ? '✓' :
    approval.status === 'Rejected' ? '✗' : approval.approvalLevel.toString();

  return (
    <div className={`st-level-row ${isMine ? 'st-level-row--mine' : ''}`}>
      <div className={`st-level-icon ${iconClass}`}>{icon}</div>
      <div className="st-level-info">
        <div className="st-level-title">
          {t('stockTransfer.approvalLevel')} {approval.approvalLevel}
          {isMine && <span className="st-level-mine-badge" style={{ marginLeft: 8 }}>{t('stockTransfer.yourApproval')}</span>}
        </div>
        <div className="st-level-sub">
          {approval.status === 'Approved' && (
            <span>✓ {t('stockTransfer.approvedBy')}: {approval.approvingUserName || approval.approvingUserId}
              {approval.approvedAt && ` · ${new Date(approval.approvedAt).toLocaleDateString()}`}
              {approval.approvalComments && ` · "${approval.approvalComments}"`}
            </span>
          )}
          {approval.status === 'Rejected' && (
            <span style={{ color: '#991b1b' }}>✗ {t('stockTransfer.rejectedBy')}: {approval.approvingUserName}
              {approval.rejectionReason && ` · ${approval.rejectionReason}`}
            </span>
          )}
          {approval.status === 'Pending' && (
            <span>{t('stockTransfer.assignedTo')}: {approval.assignedToUserName || approval.assignedToUserId}</span>
          )}
        </div>
      </div>
      {isMine && (
        <div className="st-level-actions">
          <button className="ims-action-btn ims-action-btn--approve" onClick={() => onApprove(approval.id)}>
            {t('common.approve')}
          </button>
          <button className="ims-action-btn ims-action-btn--reject" onClick={() => onReject(approval.id)}>
            {t('common.reject')}
          </button>
        </div>
      )}
    </div>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────

const StockTransferDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user } = useAuth();

  const [transfer, setTransfer]             = useState<StockTransferDTO | null>(null);
  const [approvalStatus, setApprovalStatus] = useState<StockTransferApprovalStatusDTO | null>(null);
  const [loading, setLoading]               = useState(true);
  const [actionLoading, setActionLoading]   = useState(false);
  const [pdfBusy, setPdfBusy]               = useState(false);
  const [error, setError]                   = useState<string | null>(null);

  // Modal states
  const [showApproveModal,     setShowApproveModal]     = useState(false);
  const [showRejectModal,      setShowRejectModal]      = useState(false);
  const [showTransferOutModal, setShowTransferOutModal] = useState(false);
  const [showTransferInModal,  setShowTransferInModal]  = useState(false);
  const [pendingApprovalId,    setPendingApprovalId]    = useState<number | null>(null);
  const [approveComments,      setApproveComments]      = useState('');
  const [rejectReason,         setRejectReason]         = useState('');

  const [outQtys, setOutQtys] = useState<Record<number, number>>({});
  const [inQtys,  setInQtys]  = useState<Record<number, number>>({});
  const [inUnits,         setInUnits]         = useState<Record<number, number>>({});
  const [itemConversions, setItemConversions] = useState<Record<number, UnitConversion[]>>({});

  const userId = user?.id ?? 0;

  const fetchAll = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const [tRes, aRes] = await Promise.all([
        stockTransferService.getById(id),
        stockTransferService.getApprovalStatus(id),
      ]);
      const tr = tRes.data?.data;
      if (tr) {
        setTransfer(tr);
        const outMap:  Record<number, number> = {};
        const inMap:   Record<number, number> = {};
        const unitMap: Record<number, number> = {};
        tr.items.forEach(i => {
          outMap[i.id]  = i.quantityRequested;
          inMap[i.id]   = i.quantityTransferred - i.quantityReceived;
          unitMap[i.id] = i.unitId;
        });
        setOutQtys(outMap);
        setInQtys(inMap);
        setInUnits(unitMap);
      }
      const a = aRes.data?.data;
      if (a) setApprovalStatus(a);
    } catch {
      setError(t('stockTransfer.failedToLoad'));
    } finally {
      setLoading(false);
    }
  }, [id, t]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  useEffect(() => {
    if (!showTransferInModal || !transfer) return;
    const uniqueUnitIds = [...new Set(transfer.items.map(i => i.unitId))];
    uniqueUnitIds.forEach(async (unitId) => {
      if (itemConversions[unitId]) return;
      try {
        const convs = await unitConversionService.getConversionsByFromUnit(unitId) ?? [];
        setItemConversions(prev => ({ ...prev, [unitId]: convs }));
      } catch {
        setItemConversions(prev => ({ ...prev, [unitId]: [] }));
      }
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showTransferInModal]);

  const myPendingApproval = approvalStatus?.approvals.find(
    a => a.assignedToUserId === userId && a.status === 'Pending'
  ) ?? null;

  // ── Actions ───────────────────────────────────────────────────────────────

  const handleSubmit = async () => {
    if (!id) return;
    setActionLoading(true);
    try {
      await stockTransferService.submit(id, userId);
      await fetchAll();
    } catch (e: any) {
      setError(getServerErrorMessage(e) ?? t('stockTransfer.failedToSubmit'));
    } finally {
      setActionLoading(false);
    }
  };

  const handleApproveOpen = (approvalId: number) => {
    setPendingApprovalId(approvalId);
    setApproveComments('');
    setShowApproveModal(true);
  };

  const handleRejectOpen = (approvalId: number) => {
    setPendingApprovalId(approvalId);
    setRejectReason('');
    setShowRejectModal(true);
  };

  const handleApproveConfirm = async () => {
    if (!pendingApprovalId) return;
    setActionLoading(true);
    try {
      await stockTransferService.approve(pendingApprovalId, userId, approveComments || undefined);
      setShowApproveModal(false);
      await fetchAll();
    } catch (e: any) {
      setError(getServerErrorMessage(e) ?? t('common.error'));
    } finally {
      setActionLoading(false);
    }
  };

  const handleRejectConfirm = async () => {
    if (!pendingApprovalId || !rejectReason.trim()) return;
    setActionLoading(true);
    try {
      await stockTransferService.reject(pendingApprovalId, userId, rejectReason);
      setShowRejectModal(false);
      await fetchAll();
    } catch (e: any) {
      setError(getServerErrorMessage(e) ?? t('common.error'));
    } finally {
      setActionLoading(false);
    }
  };

  const handleTransferOut = async () => {
    if (!id || !transfer) return;
    setActionLoading(true);
    try {
      const items: TransferOutItemRequest[] = transfer.items.map(i => ({
        stockTransferItemId: i.id,
        quantityTransferred: outQtys[i.id] ?? i.quantityRequested,
      }));
      const res = await stockTransferService.transferOut(id, userId, items);
      if (res.data?.data) setTransfer(res.data.data);
      setShowTransferOutModal(false);
    } catch (e: any) {
      setError(getServerErrorMessage(e) ?? t('stockTransfer.failedToTransferOut'));
    } finally {
      setActionLoading(false);
    }
  };

  const handleTransferIn = async () => {
    if (!id || !transfer) return;
    setActionLoading(true);
    try {
      const items: TransferInItemRequest[] = transfer.items
        .filter(i => (inQtys[i.id] ?? 0) > 0)
        .map(i => {
          const selectedUnit = inUnits[i.id] ?? i.unitId;
          return {
            stockTransferItemId: i.id,
            quantityReceived:    inQtys[i.id] ?? 0,
            receivedUnitId: selectedUnit !== i.unitId ? selectedUnit : undefined,
          };
        });
      const res = await stockTransferService.transferIn(id, userId, items);
      if (res.data?.data) setTransfer(res.data.data);
      setShowTransferInModal(false);
    } catch (e: any) {
      setError(getServerErrorMessage(e) ?? t('stockTransfer.failedToTransferIn'));
    } finally {
      setActionLoading(false);
    }
  };

  const handlePrintDispatchNote = async () => {
    if (!id) return;
    setPdfBusy(true);
    setError(null);
    try { await stockTransferService.openTransferOutPdf(id); }
    catch (e: any) { setError(e?.message ?? t('stockTransfer.failedToPrintDispatch')); }
    finally { setPdfBusy(false); }
  };

  const handlePrintReceiptNote = async () => {
    if (!id) return;
    setPdfBusy(true);
    setError(null);
    try { await stockTransferService.openTransferInPdf(id); }
    catch (e: any) { setError(e?.message ?? t('stockTransfer.failedToPrintReceipt')); }
    finally { setPdfBusy(false); }
  };

  const handleCancel = async () => {
    if (!id || !window.confirm(t('stockTransfer.confirmCancel'))) return;
    setActionLoading(true);
    try {
      const res = await stockTransferService.cancel(id, userId);
      if (res.data?.data) setTransfer(res.data.data);
    } catch (e: any) {
      setError(getServerErrorMessage(e) ?? t('common.error'));
    } finally {
      setActionLoading(false);
    }
  };

  // ── Loading / not-found ───────────────────────────────────────────────────

  if (loading) {
    return (
      <Layout>
        <div className="create-container">
          <div style={{ display: 'flex', justifyContent: 'center', padding: '80px 0' }}>
            <div className="spinner-border" role="status">
              <span className="visually-hidden">{t('common.loading')}</span>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  if (!transfer) {
    return (
      <Layout>
        <div className="create-container">
          <div className="ims-alert ims-alert-error">{t('stockTransfer.notFound')}</div>
        </div>
      </Layout>
    );
  }

  // ── Derived state ─────────────────────────────────────────────────────────

  const status        = transfer.status as StockTransferStatus;
  const canEdit       = status === 'Draft';
  const canSubmit     = status === 'Draft' || status === 'Rejected';
  const canTransferOut = status === 'Approved';
  const canTransferIn  = status === 'TransferredOut' || status === 'PartiallyReceived';
  const canCancel     = ['Draft', 'PendingApproval', 'Approved', 'Rejected'].includes(status);
  const isCancelled   = status === 'Cancelled';
  const showPrintDispatch = ['TransferredOut', 'PartiallyReceived', 'Completed'].includes(status);
  const showPrintReceipt  = ['PartiallyReceived', 'Completed'].includes(status);

  type StepDef = { label: string; done: boolean; rejected?: boolean };
  const steps: StepDef[] = [
    { label: t('stockTransfer.workflow.draft'),       done: true },
    { label: t('stockTransfer.workflow.approval'),    done: ['Approved','TransferredOut','PartiallyReceived','Completed'].includes(status), rejected: status === 'Rejected' },
    { label: t('stockTransfer.workflow.transferOut'), done: ['TransferredOut','PartiallyReceived','Completed'].includes(status) },
    { label: t('stockTransfer.workflow.transferIn'),  done: status === 'Completed' },
  ];

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <Layout>
      <div className="create-container st-detail-page">

        {/* ── Top Bar ── */}
        <div className="ims-top-bar">
          <div className="ims-top-bar-left">
            <button type="button" className="ims-back-btn" onClick={() => navigate('/stock-transfers')}>
              <ArrowLeft size={16} strokeWidth={2} />
            </button>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                <span className="ims-page-title">{transfer.transferNumber}</span>
                <span className={STATUS_CLASS[status] || 'ims-badge'}>{STATUS_LABEL[status] ?? status}</span>
              </div>
              <div className="st-topbar-route">
                {transfer.sourceWarehouseName}
                <ArrowRight size={12} strokeWidth={2} style={{ flexShrink: 0 }} />
                {transfer.destinationWarehouseName}
              </div>
            </div>
          </div>
          <div className="ims-top-bar-right">
            {canEdit && (
              <button type="button" className="ims-btn ims-btn-secondary"
                onClick={() => navigate(`/stock-transfers/${id}/edit`)}>
                <Edit2 size={14} strokeWidth={1.75} /> {t('common.edit')}
              </button>
            )}
            {canSubmit && (
              <button type="button" className="ims-btn ims-btn-primary"
                onClick={handleSubmit} disabled={actionLoading}>
                {actionLoading
                  ? <><span className="ims-btn-spinner" />{t('common.saving')}</>
                  : <><Send size={14} strokeWidth={1.75} />{t('stockTransfer.submitForApproval')}</>}
              </button>
            )}
            {status === 'PendingApproval' && !myPendingApproval && (
              <span className="st-awaiting-pill">
                <Clock size={13} strokeWidth={2} /> {t('stockTransfer.awaitingApproval')}
              </span>
            )}
            {canTransferOut && (
              <button type="button" className="ims-btn st-btn-transfer-out"
                onClick={() => setShowTransferOutModal(true)} disabled={actionLoading}>
                <Package size={14} strokeWidth={1.75} /> {t('stockTransfer.executeTransferOut')}
              </button>
            )}
            {canTransferIn && (
              <button type="button" className="ims-btn st-btn-transfer-in"
                onClick={() => setShowTransferInModal(true)} disabled={actionLoading}>
                <PackageOpen size={14} strokeWidth={1.75} /> {t('stockTransfer.executeTransferIn')}
              </button>
            )}
            {canCancel && (
              <button type="button" className="ims-btn ims-btn-danger"
                onClick={handleCancel} disabled={actionLoading}>
                <XCircle size={14} strokeWidth={1.75} /> {t('stockTransfer.cancel')}
              </button>
            )}
          </div>
        </div>

        {/* ── Progress stepper ── */}
        {!isCancelled ? (
          <div className="st-stepper">
            {steps.map((step, i) => (
              <React.Fragment key={step.label}>
                <div className={[
                  'st-step',
                  step.done    ? 'st-step--done'     : '',
                  step.rejected ? 'st-step--rejected' : '',
                ].join(' ')}>
                  <div className="st-step-circle">
                    {step.rejected ? '✕' : step.done ? <Check size={13} strokeWidth={3} /> : <span>{i + 1}</span>}
                  </div>
                  <span className="st-step-label">{step.label}</span>
                </div>
                {i < steps.length - 1 && (
                  <div className={`st-step-connector ${steps[i + 1]?.done || steps[i + 1]?.rejected ? 'st-step-connector--done' : ''}`} />
                )}
              </React.Fragment>
            ))}
          </div>
        ) : (
          <div className="st-cancelled-bar">
            <XCircle size={15} strokeWidth={2} />
            {t('stockTransfer.transferCancelled')}
          </div>
        )}

        {/* ── Error ── */}
        {error && (
          <div className="alert alert-danger alert-dismissible fade show" style={{ marginBottom: '16px' }}>
            {error}
            <button type="button" className="btn-close" onClick={() => setError(null)} />
          </div>
        )}

        {/* ── Pending-approval banner ── */}
        {myPendingApproval && (
          <div className="st-pending-banner" style={{ marginBottom: '16px' }}>
            <Clock size={15} strokeWidth={2} /> {t('stockTransfer.pendingYourApproval')}
          </div>
        )}

        {/* ── Main content ── */}
        <div className="create-content">

          {/* ── Left column ── */}
          <div className="create-main-section">

            {/* Transfer details */}
            <div className="create-card">
              <div className="create-card-header">
                <h2 className="create-card-title">{t('stockTransfer.transferDetails')}</h2>
              </div>
              <div className="create-card-body">
                <div className="ims-info-grid">
                  <div className="ims-info-field">
                    <span className="ims-info-label">{t('stockTransfer.source')}</span>
                    <span className="ims-info-value">{transfer.sourceWarehouseName}</span>
                  </div>
                  <div className="ims-info-field">
                    <span className="ims-info-label">{t('stockTransfer.destination')}</span>
                    <span className="ims-info-value">{transfer.destinationWarehouseName}</span>
                  </div>
                  <div className="ims-info-field">
                    <span className="ims-info-label">{t('stockTransfer.transferDate')}</span>
                    <span className="ims-info-value">{new Date(transfer.transferDate).toLocaleDateString()}</span>
                  </div>
                  {transfer.expectedArrivalDate && (
                    <div className="ims-info-field">
                      <span className="ims-info-label">{t('stockTransfer.expectedArrival')}</span>
                      <span className="ims-info-value">{new Date(transfer.expectedArrivalDate).toLocaleDateString()}</span>
                    </div>
                  )}
                  <div className="ims-info-field">
                    <span className="ims-info-label">{t('stockTransfer.requestedBy')}</span>
                    <span className="ims-info-value">{transfer.requestedByName}</span>
                  </div>
                  {transfer.transferredByName && (
                    <div className="ims-info-field">
                      <span className="ims-info-label">{t('stockTransfer.transferredBy')}</span>
                      <span className="ims-info-value">
                        {transfer.transferredByName}
                        {transfer.transferredOutAt && (
                          <span className="st-info-date">{new Date(transfer.transferredOutAt).toLocaleDateString()}</span>
                        )}
                      </span>
                    </div>
                  )}
                  {transfer.receivedByName && (
                    <div className="ims-info-field">
                      <span className="ims-info-label">{t('stockTransfer.receivedBy')}</span>
                      <span className="ims-info-value">
                        {transfer.receivedByName}
                        {transfer.receivedAt && (
                          <span className="st-info-date">{new Date(transfer.receivedAt).toLocaleDateString()}</span>
                        )}
                      </span>
                    </div>
                  )}
                  {transfer.notes && (
                    <div className="ims-info-field" style={{ gridColumn: '1 / -1' }}>
                      <span className="ims-info-label">{t('common.notes')}</span>
                      <span className="ims-info-value">{transfer.notes}</span>
                    </div>
                  )}
                  {transfer.rejectionNotes && (
                    <div className="ims-info-field" style={{ gridColumn: '1 / -1' }}>
                      <span className="ims-info-label">{t('stockTransfer.rejectionNotes')}</span>
                      <span className="ims-info-value st-rejection-text">{transfer.rejectionNotes}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Items table */}
            <div className="create-card">
              <div className="create-card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h2 className="create-card-title">{t('stockTransfer.items')}</h2>
                <span className="st-item-count-badge">
                  {transfer.items.length} {transfer.items.length === 1 ? 'item' : t('stockTransfer.items').toLowerCase()}
                </span>
              </div>
              <div style={{ overflowX: 'auto' }}>
                <table className="ims-table st-items-table">
                  <thead>
                    <tr>
                      <th style={{ width: '40px' }}>#</th>
                      <th>{t('stockTransfer.product')}</th>
                      <th style={{ width: '90px' }}>{t('stockTransfer.unit')}</th>
                      <th style={{ textAlign: 'right', width: '90px' }}>{t('stockTransfer.requested')}</th>
                      <th style={{ textAlign: 'right', width: '100px' }}>{t('stockTransfer.transferred')}</th>
                      <th style={{ textAlign: 'right', width: '90px' }}>{t('stockTransfer.received')}</th>
                      <th style={{ textAlign: 'right', width: '85px' }}>{t('stockTransfer.variance')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {transfer.items.map((item, idx) => (
                      <tr key={item.id}>
                        <td className="st-row-num">{idx + 1}</td>
                        <td>
                          <div className="st-product-name">{item.productName}</div>
                          {item.productCode && (
                            <div className="st-product-code">{item.productCode}</div>
                          )}
                        </td>
                        <td className="st-unit-name">{item.unitName}</td>
                        <td style={{ textAlign: 'right', fontWeight: 600 }}>{item.quantityRequested}</td>
                        <td style={{ textAlign: 'right', color: item.quantityTransferred > 0 ? 'var(--ims-text-main)' : 'var(--ims-text-muted)' }}>
                          {item.quantityTransferred || '—'}
                        </td>
                        <td style={{ textAlign: 'right', color: item.quantityReceived > 0 ? 'var(--ims-text-main)' : 'var(--ims-text-muted)' }}>
                          {item.quantityReceived || '—'}
                        </td>
                        <td style={{ textAlign: 'right' }}>
                          {item.quantityTransferred > 0 ? (
                            <span className={
                              item.varianceQuantity > 0 ? 'variance-positive' :
                              item.varianceQuantity < 0 ? 'variance-negative' : 'variance-zero'
                            }>
                              {item.varianceQuantity > 0 ? '+' : ''}{item.varianceQuantity}
                            </span>
                          ) : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Approval levels */}
            {approvalStatus && approvalStatus.approvals.length > 0 && (
              <div className="create-card">
                <div className="create-card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <h2 className="create-card-title">{t('stockTransfer.approvalLevels')}</h2>
                  <span style={{ fontSize: '12px', color: 'var(--ims-text-muted)' }}>
                    {approvalStatus.currentApprovalLevel}/{approvalStatus.totalApprovalLevels} {t('stockTransfer.levelsCompleted')}
                  </span>
                </div>
                <div className="create-card-body">
                  <div className="st-levels-list">
                    {approvalStatus.approvals.map(a => (
                      <ApprovalLevelRow
                        key={a.id}
                        approval={a}
                        currentUserId={userId}
                        onApprove={handleApproveOpen}
                        onReject={handleRejectOpen}
                      />
                    ))}
                  </div>
                </div>
              </div>
            )}

          </div>

          {/* ── Right sidebar ── */}
          <div className="create-sidebar">

            {/* Route summary */}
            <div className="create-card">
              <div className="create-card-header">
                <h3 className="create-card-title">{t('stockTransfer.transferSummary')}</h3>
              </div>
              <div className="create-card-body">
                <div className="st-route-card">
                  <div className="st-route-node">{transfer.sourceWarehouseName}</div>
                  <div className="st-route-arrow-col">
                    <div className="st-route-line" />
                    <ArrowRight size={14} strokeWidth={2} className="st-route-icon" />
                    <div className="st-route-line" />
                  </div>
                  <div className="st-route-node">{transfer.destinationWarehouseName}</div>
                </div>
                <div className="st-summary-fields">
                  <div className="st-summary-row">
                    <span className="st-summary-label">{t('stockTransfer.items')}</span>
                    <span className="st-summary-value">{transfer.items.length}</span>
                  </div>
                  <div className="st-summary-row">
                    <span className="st-summary-label">{t('stockTransfer.requestedBy')}</span>
                    <span className="st-summary-value">{transfer.requestedByName}</span>
                  </div>
                  <div className="st-summary-row">
                    <span className="st-summary-label">{t('stockTransfer.transferDate')}</span>
                    <span className="st-summary-value">{new Date(transfer.transferDate).toLocaleDateString()}</span>
                  </div>
                  {transfer.expectedArrivalDate && (
                    <div className="st-summary-row">
                      <span className="st-summary-label">{t('stockTransfer.expectedArrival')}</span>
                      <span className="st-summary-value">{new Date(transfer.expectedArrivalDate).toLocaleDateString()}</span>
                    </div>
                  )}
                  {transfer.transferredByName && (
                    <div className="st-summary-row">
                      <span className="st-summary-label">{t('stockTransfer.transferredBy')}</span>
                      <span className="st-summary-value">{transfer.transferredByName}</span>
                    </div>
                  )}
                  {transfer.receivedByName && (
                    <div className="st-summary-row">
                      <span className="st-summary-label">{t('stockTransfer.receivedBy')}</span>
                      <span className="st-summary-value">{transfer.receivedByName}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Print */}
            {(showPrintDispatch || showPrintReceipt) && (
              <div className="create-card">
                <div className="create-card-header">
                  <h3 className="create-card-title">{t('common.print')}</h3>
                </div>
                <div className="create-card-body">
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {showPrintDispatch && (
                      <button
                        className="ims-action-btn ims-action-btn--print st-print-btn"
                        onClick={handlePrintDispatchNote}
                        disabled={pdfBusy || actionLoading}
                      >
                        {pdfBusy
                          ? <><span className="ims-btn-spinner" />{t('stockTransfer.generatingPdf')}</>
                          : <><Printer size={14} strokeWidth={1.75} />{t('stockTransfer.printDispatchNote')}</>}
                      </button>
                    )}
                    {showPrintReceipt && (
                      <button
                        className="ims-action-btn ims-action-btn--print st-print-btn"
                        onClick={handlePrintReceiptNote}
                        disabled={pdfBusy || actionLoading}
                      >
                        {pdfBusy
                          ? <><span className="ims-btn-spinner" />{t('stockTransfer.generatingPdf')}</>
                          : <><Printer size={14} strokeWidth={1.75} />{t('stockTransfer.printReceiptNote')}</>}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            )}

          </div>
        </div>
      </div>

      {/* ── Approve Modal ── */}
      {showApproveModal && (
        <div className="ims-modal-overlay" onClick={() => setShowApproveModal(false)}>
          <div className="ims-modal" onClick={e => e.stopPropagation()}>
            <div className="ims-modal-header">
              <h3>{t('stockTransfer.approveTitle')}</h3>
              <button className="ims-modal-close" onClick={() => setShowApproveModal(false)}>×</button>
            </div>
            <div className="ims-modal-body">
              <div className="ims-form-group">
                <label className="ims-form-label">{t('stockTransfer.approvalComments')} ({t('common.optional')})</label>
                <textarea
                  className="ims-form-control"
                  rows={3}
                  value={approveComments}
                  onChange={e => setApproveComments(e.target.value)}
                  placeholder={t('stockTransfer.approvalCommentsPlaceholder')}
                />
              </div>
            </div>
            <div className="ims-modal-footer">
              <button className="btn-cancel" onClick={() => setShowApproveModal(false)}>{t('common.cancel')}</button>
              <button className="ims-btn ims-btn-success" onClick={handleApproveConfirm} disabled={actionLoading}>
                {actionLoading ? '…' : t('common.approve')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Reject Modal ── */}
      {showRejectModal && (
        <div className="ims-modal-overlay" onClick={() => setShowRejectModal(false)}>
          <div className="ims-modal" onClick={e => e.stopPropagation()}>
            <div className="ims-modal-header">
              <h3>{t('stockTransfer.rejectTitle')}</h3>
              <button className="ims-modal-close" onClick={() => setShowRejectModal(false)}>×</button>
            </div>
            <div className="ims-modal-body">
              <div className="ims-form-group">
                <label className="ims-form-label">{t('stockTransfer.rejectionReason')} <span className="required">*</span></label>
                <textarea
                  className="ims-form-control"
                  rows={3}
                  value={rejectReason}
                  onChange={e => setRejectReason(e.target.value)}
                  placeholder={t('stockTransfer.rejectionReasonPlaceholder')}
                />
              </div>
            </div>
            <div className="ims-modal-footer">
              <button className="btn-cancel" onClick={() => setShowRejectModal(false)}>{t('common.cancel')}</button>
              <button className="btn-delete" onClick={handleRejectConfirm} disabled={!rejectReason.trim() || actionLoading}>
                {actionLoading ? '…' : t('common.reject')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Transfer Out Modal ── */}
      {showTransferOutModal && transfer && (
        <div className="ims-modal-overlay" onClick={() => setShowTransferOutModal(false)}>
          <div className="ims-modal ims-modal--wide" onClick={e => e.stopPropagation()}>
            <div className="ims-modal-header">
              <h3>{t('stockTransfer.transferOutTitle')}</h3>
              <button className="ims-modal-close" onClick={() => setShowTransferOutModal(false)}>×</button>
            </div>
            <div className="ims-modal-body">
              <p style={{ marginBottom: 12, color: 'var(--ims-text-sub)', fontSize: '0.875rem' }}>
                {t('stockTransfer.transferOutDesc', { warehouse: transfer.sourceWarehouseName })}
              </p>
              <table className="st-transfer-in-table">
                <thead>
                  <tr>
                    <th>{t('stockTransfer.product')}</th>
                    <th style={{ textAlign: 'right' }}>{t('stockTransfer.requested')}</th>
                    <th style={{ width: 130, textAlign: 'right' }}>{t('stockTransfer.quantityToTransfer')}</th>
                  </tr>
                </thead>
                <tbody>
                  {transfer.items.map(item => (
                    <tr key={item.id}>
                      <td>{item.productName}</td>
                      <td style={{ textAlign: 'right' }}>{item.quantityRequested}</td>
                      <td>
                        <input
                          type="number"
                          className="st-transfer-in-input"
                          value={outQtys[item.id] ?? item.quantityRequested}
                          min={0}
                          max={item.quantityRequested}
                          onChange={e => setOutQtys(prev => ({ ...prev, [item.id]: Number(e.target.value) }))}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="ims-modal-footer">
              <button className="btn-cancel" onClick={() => setShowTransferOutModal(false)}>{t('common.cancel')}</button>
              <button className="ims-btn ims-btn-primary" onClick={handleTransferOut} disabled={actionLoading}>
                {actionLoading ? '…' : t('stockTransfer.confirmTransferOut')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Transfer In Modal ── */}
      {showTransferInModal && transfer && (
        <div className="ims-modal-overlay" onClick={() => setShowTransferInModal(false)}>
          <div className="ims-modal ims-modal--wide" onClick={e => e.stopPropagation()}>
            <div className="ims-modal-header">
              <h3>{t('stockTransfer.transferInTitle')}</h3>
              <button className="ims-modal-close" onClick={() => setShowTransferInModal(false)}>×</button>
            </div>
            <div className="ims-modal-body">
              <p style={{ marginBottom: 12, color: 'var(--ims-text-sub)', fontSize: '0.875rem' }}>
                {t('stockTransfer.transferInDesc', { warehouse: transfer.destinationWarehouseName })}
              </p>
              <table className="st-transfer-in-table">
                <thead>
                  <tr>
                    <th>{t('stockTransfer.product')}</th>
                    <th style={{ textAlign: 'right' }}>{t('stockTransfer.transferred')}</th>
                    <th style={{ textAlign: 'right' }}>{t('stockTransfer.alreadyReceived')}</th>
                    <th style={{ width: 150 }}>{t('stockTransfer.unit')}</th>
                    <th style={{ width: 130, textAlign: 'right' }}>{t('stockTransfer.quantityToReceive')}</th>
                  </tr>
                </thead>
                <tbody>
                  {transfer.items.map(item => {
                    const remaining   = item.quantityTransferred - item.quantityReceived;
                    const baseUnitId  = item.unitId;
                    const convs       = itemConversions[baseUnitId] ?? [];
                    const selectedUnit = inUnits[item.id] ?? baseUnitId;
                    const hasConversions = convs.length > 0;

                    return (
                      <tr key={item.id}>
                        <td>{item.productName}</td>
                        <td style={{ textAlign: 'right' }}>{item.quantityTransferred}</td>
                        <td style={{ textAlign: 'right' }}>{item.quantityReceived}</td>
                        <td>
                          {hasConversions ? (
                            <select
                              className="st-transfer-in-input"
                              value={selectedUnit}
                              onChange={e => setInUnits(prev => ({ ...prev, [item.id]: Number(e.target.value) }))}
                            >
                              <option value={baseUnitId}>{item.unitName}</option>
                              {convs.map(c => (
                                <option key={c.toUnitId} value={c.toUnitId}>{c.toUnitName}</option>
                              ))}
                            </select>
                          ) : (
                            <span style={{ fontSize: '0.875rem' }}>{item.unitName}</span>
                          )}
                        </td>
                        <td>
                          <input
                            type="number"
                            className="st-transfer-in-input"
                            value={inQtys[item.id] ?? remaining}
                            min={0}
                            onChange={e => setInQtys(prev => ({ ...prev, [item.id]: Number(e.target.value) }))}
                          />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div className="ims-modal-footer">
              <button className="btn-cancel" onClick={() => setShowTransferInModal(false)}>{t('common.cancel')}</button>
              <button className="ims-btn ims-btn-success" onClick={handleTransferIn} disabled={actionLoading}>
                {actionLoading ? '…' : t('stockTransfer.confirmTransferIn')}
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default StockTransferDetailPage;
